import { Copy, FileText } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";
import { useIssuedReports, useReportKinds } from "../api/queries";
import { useLocale, useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { useToast } from "../ui/Toasts";
import { reportsMessages } from "./reports.i18n";

const DAY = 86_400_000;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Relatórios de evidência (ADR 0026): período, formato e um clique por relatório. */
export function ReportsPage() {
  const { orgId = "" } = useParams();
  const kinds = useReportKinds(orgId);
  const issued = useIssuedReports(orgId);
  const t = useT(reportsMessages);
  const locale = useLocale();
  const f = useFormat();
  const toast = useToast();
  const today = new Date();
  const [from, setFrom] = useState(isoDate(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))));
  const [to, setTo] = useState(isoDate(today));
  const [format, setFormat] = useState<"pdf" | "csv">("pdf");

  function preset(which: "thisMonth" | "lastMonth" | "last30" | "quarter") {
    const y = today.getUTCFullYear(), m = today.getUTCMonth();
    switch (which) {
      case "thisMonth":
        setFrom(isoDate(new Date(Date.UTC(y, m, 1)))); setTo(isoDate(today)); break;
      case "lastMonth":
        setFrom(isoDate(new Date(Date.UTC(y, m - 1, 1)))); setTo(isoDate(new Date(Date.UTC(y, m, 0)))); break;
      case "last30":
        setFrom(isoDate(new Date(today.getTime() - 30 * DAY))); setTo(isoDate(today)); break;
      case "quarter":
        setFrom(isoDate(new Date(today.getTime() - 90 * DAY))); setTo(isoDate(today)); break;
    }
  }

  const periodOk = from <= to && new Date(to).getTime() - new Date(from).getTime() <= 366 * DAY;

  function issue(kind: string) {
    if (!periodOk) {
      toast("error", t.invalidPeriod);
      return;
    }
    // O servidor gera e registra a emissão; o navegador grava o arquivo.
    const q = new URLSearchParams({ from, to, format, locale });
    window.location.assign(`/api/v1/orgs/${encodeURIComponent(orgId)}/reports/${encodeURIComponent(kind)}?${q.toString()}`);
    toast("ok", t.started(t.kinds[kind]?.title ?? kind));
    setTimeout(() => void issued.refetch(), 2_500);
  }

  async function copyHash(hash: string) {
    try {
      await navigator.clipboard.writeText(hash);
      toast("ok", t.hashCopied);
    } catch {
      /* sem área de transferência: o hash está na tela */
    }
  }

  return (
    <>
      <PageHead title={t.title} lede={t.lede} />

      <Panel title={t.period}>
        <div style={{ display: "grid", gap: 14 }}>
          <div className="segmented" role="group" aria-label={t.period}>
            <button type="button" onClick={() => preset("thisMonth")}>{t.presetThisMonth}</button>
            <button type="button" onClick={() => preset("lastMonth")}>{t.presetLastMonth}</button>
            <button type="button" onClick={() => preset("last30")}>{t.presetLast30}</button>
            <button type="button" onClick={() => preset("quarter")}>{t.presetQuarter}</button>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="rel-de">{t.from}</label>
              <input id="rel-de" className="input num" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-invalid={!periodOk} />
            </div>
            <div className="field">
              <label htmlFor="rel-ate">{t.to}</label>
              <input id="rel-ate" className="input num" type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-invalid={!periodOk} />
            </div>
            <div className="field">
              <span className="label" id="rel-formato">{t.format}</span>
              <div className="segmented" role="group" aria-labelledby="rel-formato">
                <button type="button" aria-pressed={format === "pdf"} onClick={() => setFormat("pdf")}>{t.formatPdf}</button>
                <button type="button" aria-pressed={format === "csv"} onClick={() => setFormat("csv")}>{t.formatCsv}</button>
              </div>
              <span className="hint">{t.csvNote}</span>
            </div>
          </div>
          {!periodOk && <span className="error">{t.invalidPeriod}</span>}
        </div>
      </Panel>

      <section className="panel">
        {kinds.isPending ? <LoadingRows /> : kinds.isError ? <div className="panel-body"><ErrorState error={kinds.error} /></div> : (
          <ul className="panel-body" style={{ listStyle: "none", margin: 0, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(min(300px, 100%), 1fr))" }}>
            {kinds.data.map((kind) => {
              const k = t.kinds[kind] ?? { title: kind, desc: "", controls: "" };
              return (
                <li key={kind} style={{ display: "grid", gap: 8, padding: 14, border: "1px solid var(--line)", borderRadius: 10, alignContent: "start" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
                    <strong>{k.title}</strong>
                    <span className="tag">{k.controls}</span>
                  </div>
                  <p className="ink-2" style={{ fontSize: "var(--fs-13)", margin: 0 }}>{k.desc}</p>
                  <button className="btn btn-sm" type="button" style={{ justifySelf: "start" }} onClick={() => issue(kind)} aria-label={`${t.issue}: ${k.title}`}>
                    <FileText aria-hidden="true" /> {t.issue}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Panel title={t.issued} sub={t.issuedHint}>
        {issued.isPending ? <LoadingRows /> : issued.isError ? <ErrorState error={issued.error} /> : issued.data.length === 0 ? (
          <p className="muted">{t.noIssued}</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>{t.colReport}</th><th>{t.colPeriod}</th><th>{t.colFormat}</th><th>{t.colIssuedBy}</th><th>{t.colIssuedAt}</th><th>{t.colHash}</th></tr></thead>
              <tbody>
                {issued.data.map((i) => (
                  <tr key={i.id}>
                    <td><strong>{t.kinds[i.kind]?.title ?? i.kind}</strong></td>
                    <td className="num">{i.from} – {i.to}</td>
                    <td>{i.format === "pdf" ? t.formatPdf : t.formatCsv} · {f.bytes(i.bytes)}</td>
                    <td>{i.issuedBy}</td>
                    <td className="num">{f.dateTime(i.issuedAt)}</td>
                    <td>
                      <span className="mono" title={i.sha256}>{i.sha256.slice(0, 16)}…</span>{" "}
                      <button className="btn btn-sm" type="button" onClick={() => copyHash(i.sha256)} aria-label={`${t.copyHash}: ${i.id}`}>
                        <Copy aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
